import React, { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Text,
  Select,
  useColorModeValue,
  Modal,
  ModalContent,
  ModalBody,
  ModalOverlay,
  ModalHeader,
  ModalCloseButton,
  useDisclosure,
  Input,
  Button,
  useMediaQuery,
  useToast,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { QrScanner } from '@yudiel/react-qr-scanner';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import { QrCodeIConMobile } from 'components/Icons/Icons';
import { useBookingActions } from 'hooks/useBookingActions';
import moment from 'moment';
import { showTextFloor } from 'variables/columnsData';

// Helper component for info rows
const InfoRow = ({
  label,
  value,
  isMobile = false,
  boldLabel = false,
  boldValue = false,
  isBigSize = false
}) => (
  <Grid
    templateColumns={isMobile ? '130px 1fr' : '180px 1fr'}
    gap={2}
    mb={{ lg: 2, sm: 1 }}
    alignItems='center'
  >
    <GridItem>
      <Text
        fontSize='14px'
        color='#2D3748'
        fontWeight={boldLabel ? 'bold' : '500'}
      >
        {label}:
      </Text>
    </GridItem>
    <GridItem overflow='visible' minW={0}>
      {typeof value === 'string' ? (
        <Text
          fontWeight={boldValue ? 'bold' : 'normal'}
          fontSize={isBigSize ? '16px' : '14px'}
          wordBreak='break-word'
        >
          {value}
        </Text>
      ) : (
        value
      )}
    </GridItem>
  </Grid>
);

function ScanOPD() {
  const textColor = useColorModeValue('gray.700', 'white');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bookingActions = useBookingActions();
  const [theaterPassInfo, setTheaterPassInfo] = useState();
  const [showID, setShowID] = useState();
  const [listShow, setListShow] = useState([]);
  const [confirmCode, setConfirmationCode] = useState('');
  const [totalSeatNotOccupied, setTotalSeatNotOccupied] = useState();
  const [scanning, setScanning] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const scanQRRef = useRef(null);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);
  const lastScannedCodeRef = useRef('');
  const showIDRef = useRef(null);
  const successSound = new Audio('sound/success.mp3');
  const failedSound = new Audio('sound/error.mp3');
  const [isNotFound, setIsNotFound] = useState(false);
  const [cameraVisible, setCameraVisible] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const toast = useToast();

  useEffect(() => {
    showIDRef.current = showID;
  }, [showID]);

  useEffect(() => {
    const element = scanQRRef.current;
    if (element) {
      element.style.borderWith = 40;
    }

    const fetchShows = async () => {
      const res = await bookingActions.getShowsCurrentDate();

      const filteredShows = res.data.data.filter((s) => s.status === 1);
      if (filteredShows.length > 0) {
        setListShow(filteredShows);
        const ids = filteredShows.map((s) => s.id);
        if (!showIDRef.current || !ids.includes(showIDRef.current)) {
          setShowID(filteredShows[0].id);
        }
      }
    };

    fetchShows();
    setTimeout(() => setCameraVisible(true), 300);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setCameraVisible(false);
      } else {
        fetchShows();
        setTimeout(() => setCameraVisible(true), 300);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);


  useEffect(() => {
    let countSeatNotOccupied = 0;
    if (theaterPassInfo && theaterPassInfo.booking_detail) {
      theaterPassInfo.booking_detail.forEach((i) => {
        if (i.status === 0 || i.status === 1) {
          countSeatNotOccupied++;
        }
      });
      setTotalSeatNotOccupied(countSeatNotOccupied);
    }
  }, [theaterPassInfo]);

  const scanQRCode = async (val) => {
    // Prevent duplicate calls
    if (isProcessing || !scanning) {
      return;
    }

    try {
      setIsProcessing(true);
      setIsDisabled(true);
      setTheaterPassInfo(undefined);
      setIsNotFound(false);
      lastScannedCodeRef.current = val;

      if (!val || val.length === 0) {
        toast({
          description: 'Confirm code cannot be empty.',
          status: 'error',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        setScanning(true);
        setIsDisabled(false);
        return;
      }

      if (!showID) {
        toast({
          description: 'Please wait for show data to load.',
          status: 'warning',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
        setScanning(true);
        setIsDisabled(false);
        setIsProcessing(false);
        return;
      }

      const res = await bookingActions.scanTheaterPass({
        code: val,
        id: showID,
      });
      if (res && scanning) {
        setIsNotFound(false);
        setTheaterPassInfo(res);
        setScanning(false);
        if (res.scanned_status === 1) {
          successSound
            .play()
            .catch((error) => console.error('Audio play error:', error));
        } else {
          failedSound
            .play()
            .catch((error) => console.error('Audio play error:', error));
        }
      } else {
        if (!res) {
          setIsNotFound(true);
          toast({
            description: `Theater pass not found: ${val}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
        failedSound
          .play()
          .catch((error) => console.error('Audio play error:', error));
      }
      setTimeout(() => {
        setScanning(true);
        setIsDisabled(false);
        setIsProcessing(false);
      }, 1000);
    } catch (error) {
      console.warn(error);
      setIsProcessing(false);
      setIsNotFound(true);
      toast({
        description: `Error while scanning: ${lastScannedCodeRef.current}`,
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      setTimeout(() => {
        setScanning(true);
        setIsDisabled(false);
      }, 1000);
      failedSound
        .play()
        .catch((error) => console.error('Audio play error:', error));
    }
  };

  const ticketError = (status) => {
    switch (status) {
      case 0:
        return "THIS TICKET HASN'T PRINTED THEATER PASS YET!";
      case 1:
        return 'SUCCESSFUL!';
      case 2:
        return 'ALREADY OCCUPIED!';
      case 3:
        return 'WRONG SHOW!';
    }
  };

  const ENTER_KEY = 13;

  useEffect(() => {
    let barcode = '';

    const handleKeyDown = (e) => {
      if (document.activeElement instanceof HTMLSelectElement) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        document.activeElement.blur();
      }
    };

    const handleKeyPress = async (e) => {
      if (isDisabled || !scanning || isProcessing) {
        return;
      }

      let code = e.keyCode ? e.keyCode : e.which;
      barcode += String.fromCharCode(code);

      if (code === ENTER_KEY) {
        const trimmedBarcode = barcode.trim();
        if (trimmedBarcode && !isProcessing && scanning) {
          setConfirmationCode(trimmedBarcode);
          await scanQRCode(trimmedBarcode);
        }
        barcode = '';
      }
    };

    // capture:true so handleKeyDown runs before the element's own handler
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keypress', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keypress', handleKeyPress);
    };
  }, [isDisabled, scanning, isProcessing, showID]);

  const handleShowTimeChange = (e) => {
    setShowID(parseInt(e.target.value));
    e.target.blur();
  };


  const handleReset = () => {
    setConfirmationCode('');
    setTheaterPassInfo(undefined);
    setIsNotFound(false);
  };

  return (
    <>
      <Card px='0px'>
        <CardHeader px='22px' mb='12px' display={{ sm: 'none', lg: 'block' }}>
          <Flex direction='column'>
            <Text
              float='left'
              color={textColor}
              fontSize='lg'
              fontWeight='bold'
              mb='6px'
            >
              Scan Theater Pass
            </Text>
          </Flex>
        </CardHeader>
        <CardBody px={{ sm: '8px', md: '22px' }}>
          <Grid
            templateColumns={{ sm: '1fr', md: '150px 1fr' }}
            gap={{ sm: 2, md: 3 }}
            mb='8px'
            alignItems='center'
          >
            <GridItem>
              <Text fontSize={{ sm: '14px', md: '16px' }} fontWeight='bold'>
                Show Time
              </Text>
            </GridItem>
            <GridItem>
              <Select
                variant='main'
                fontSize='sm'
                size='md'
                name='payment_status'
                maxW={{ sm: '100%', md: '500px' }}
                value={showID}
                onChange={handleShowTimeChange}
              >
                {listShow.map((item, index) => (
                  <option key={index} value={item.id}>
                    {moment(item.date).format('DD/MM/YYYY')} - {item.time.slice(0, -3)} - {item.show_schedule.show.name} -{' '}
                    {item.show_schedule.venue.name}
                  </option>
                ))}
              </Select>
            </GridItem>
          </Grid>
          <Flex
              m={'auto'}
              mb={2}
              display={{ sm: 'flex', lg: 'none' }}
              w={'100%'}
              maxW={'300px'}
              className='scanQr'
              direction='column'
              align='center'
            >
              {cameraVisible && !cameraError && (
                <QrScanner
                  className='tests'
                  onDecode={(result) => {
                    if (!isDisabled && scanning) {
                      setConfirmationCode(result);
                      scanQRCode(result);
                    }
                  }}
                  onError={(error) => {
                    console.error('QR Scanner error:', error);
                    if (
                      error?.name === 'NotAllowedError' ||
                      error?.message?.toLowerCase().includes('permission') ||
                      error?.message?.toLowerCase().includes('not allowed')
                    ) {
                      setCameraError(true);
                    }
                  }}
                  constraints={{
                    facingMode: 'environment',
                  }}
                  containerStyle={{
                    width: '100%',
                    paddingTop: '100%',
                    position: 'relative',
                  }}
                  videoStyle={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}
            </Flex>
          <Grid
            templateColumns={{ sm: '1fr', md: '150px 1fr' }}
            gap={{ sm: 2, md: 3 }}
            mb={2}
            alignItems='center'
          >
            <GridItem display={{ sm: 'none', md: 'block' }}>
              <Text fontSize={{ sm: '14px', md: '16px' }} fontWeight='bold'>
                Confirmation Code
              </Text>
            </GridItem>
            <GridItem>
              <Flex align='center' gap={2} flexWrap='wrap'>
                <Input
                  fontSize='sm'
                  type='text'
                  placeholder='Confirmation Code'
                  size='md'
                  value={confirmCode}
                  onChange={(e) => {
                    setConfirmationCode(e.target.value);
                  }}
                  maxW={{ sm: '100%', md: '300px' }}
                  ref={inputRef}
                  flex={{ sm: '1 1 100%', md: '0 1 300px' }}
                />
                {!isMobile && (
                  <QrCodeIConMobile
                    style={{
                      cursor: 'pointer',
                    }}
                    w='40px'
                    h='40px'
                    onClick={() => {
                      onOpen();
                    }}
                  />
                )}
                <Button
                  size='md'
                  colorScheme='blue'
                  fontSize='sm'
                  px={6}
                  disabled={isDisabled}
                  onClick={() => scanQRCode(confirmCode)}
                  ref={buttonRef}
                >
                  Scan
                </Button>
                <Button
                  size='md'
                  variant='outline'
                  colorScheme='gray'
                  fontSize='sm'
                  px={6}
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </Flex>
            </GridItem>
          </Grid>

          {theaterPassInfo && (
            <Flex
              direction='column'
              position='relative'
              borderRadius={'15px'}
              p={2}
              background={
                theaterPassInfo.scanned_status === 1
                  ? 'rgba(84, 220, 82, 0.50)'
                  : 'rgba(234, 115, 89, 0.50)'
              }
              maxW={{ sm: '100%', lg: '70%' }}
              minH={'115px'}
            >
              {theaterPassInfo.scanned_status === 1 ? (
                <>
                  <InfoRow
                    label='Status'
                    value={ticketError(theaterPassInfo.scanned_status)}
                    isMobile={isMobile}
                    boldValue={true}
                  />
                  <InfoRow
                    label='Confirmation Code'
                    value={theaterPassInfo.confirmation_code}
                    isMobile={isMobile}
                    boldValue={true}
                    isBigSize={true}
                  />
                  <InfoRow
                    label='First Scanned Time'
                    value={moment(theaterPassInfo?.scanned_at).format(
                      'DD/MM/YYYY, HH:mm:ss'
                    )}
                    isMobile={isMobile}
                  />
                  <InfoRow
                    label='Total Scan'
                    value='1'
                    isMobile={isMobile}
                  />
                </>
              ) : theaterPassInfo.scanned_status === 2 ? (
                <>
                  <InfoRow
                    label='Status'
                    value={ticketError(theaterPassInfo.scanned_status)}
                    isMobile={isMobile}
                    boldValue={true}
                  />
                  <InfoRow
                    label='Confirmation Code'
                    value={theaterPassInfo.confirmation_code}
                    isMobile={isMobile}
                    boldValue={true}
                    isBigSize={true}
                  />
                  {theaterPassInfo.histories && theaterPassInfo.histories.length > 0 && (
                    <>
                      <InfoRow
                        label='First Scanned Time'
                        value={moment(theaterPassInfo.histories[0]?.created_at).format(
                          'DD/MM/YYYY, HH:mm:ss'
                        )}
                        isMobile={isMobile}
                      />
                      {theaterPassInfo.histories.length > 2 &&
                        theaterPassInfo.histories.slice(1, -1).map((history) => (
                          <InfoRow
                            key={history.id}
                            label='Scanned Time'
                            value={moment(history.created_at).format(
                              'DD/MM/YYYY, HH:mm:ss'
                            )}
                            isMobile={isMobile}
                          />
                        ))}
                      {theaterPassInfo.histories.length > 1 && (
                        <InfoRow
                          label='Last Scanned Time'
                          value={moment(
                            theaterPassInfo.histories[theaterPassInfo.histories.length - 1]?.created_at
                          ).format('DD/MM/YYYY, HH:mm:ss')}
                          isMobile={isMobile}
                        />
                      )}
                      <InfoRow
                        label='Total Scan'
                        value={theaterPassInfo.histories.length.toString()}
                        isMobile={isMobile}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <InfoRow
                    label='Confirmation Code'
                    value={theaterPassInfo.confirmation_code}
                    isMobile={isMobile}
                    boldValue={true}
                    isBigSize={true}
                  />
                </>
              )}
              {theaterPassInfo.scanned_status !== 3 && (
                <>
                  <InfoRow
                    label='Show Name'
                    value={theaterPassInfo?.show?.name}
                    isMobile={isMobile}
                  />
                  <InfoRow
                    label='Show Date'
                    value={`${moment(
                      theaterPassInfo?.show_schedule_detail?.date
                    ).format('DD/MM/YYYY')} - ${theaterPassInfo?.show_schedule_detail?.time}`}
                    isMobile={isMobile}
                    boldValue={true}
                  />
                  <InfoRow
                    label='Venue'
                    value={theaterPassInfo?.venue?.name}
                    isMobile={isMobile}
                  />
                  <InfoRow
                    label='Floor'
                    value={showTextFloor(theaterPassInfo?.floor)}
                    isMobile={isMobile}
                    boldValue={true}
                    isBigSize={true}
                  />
                  <InfoRow
                    label='Zone'
                    value={theaterPassInfo?.zone?.name}
                    isMobile={isMobile}
                  />
                  <InfoRow
                    label='Seat No.'
                    value={theaterPassInfo?.seat_no}
                    isMobile={isMobile}
                    boldValue={true}
                    isBigSize={true}
                  />
                  <InfoRow
                    label='Booking No.'
                    value={theaterPassInfo?.code}
                    isMobile={isMobile}
                  />
                  <InfoRow
                    label='Seat in Booking'
                    value={
                      <Flex flexWrap='wrap' gap={1} align='baseline'>
                        <Text
                          fontSize={{ md: '16px', sm: '14px' }}
                        >
                          {totalSeatNotOccupied}/
                          {theaterPassInfo?.booking_detail?.length} seats haven't
                          been occupied yet
                        </Text>
                        {theaterPassInfo.booking_detail.map((i, index) => (
                          <Text
                            key={index}
                            fontSize={{ md: '16px', sm: '14px' }}
                            fontWeight='bold'
                            color={i.status === 2 ? 'green' : 'red'}
                          >
                            {i.seat}
                          </Text>
                        ))}
                      </Flex>
                    }
                    isMobile={isMobile}
                  />
                </>
              )}
            </Flex>
          )}
          {isNotFound && (
            <Flex
              direction='column'
              position='relative'
              borderRadius={'15px'}
              p={2}
              background={'rgba(234, 115, 89, 0.50)'}
              maxW={{ sm: '100%', lg: '70%' }}
              minH={'115px'}
            >
              <InfoRow
                label='Status'
                value='Theater pass is invalid or does not exist.'
                isMobile={isMobile}
                boldValue={true}
              />
              <InfoRow
                label='Confirmation Code'
                value={lastScannedCodeRef.current}
                isMobile={isMobile}
                boldValue={true}
                isBigSize={true}
              />
            </Flex>
          )}
        </CardBody>
      </Card>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Scan QR</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <QrScanner
              onDecode={(result) => {
                if (!isDisabled && scanning && !isProcessing) {
                  setConfirmationCode(result);
                  scanQRCode(result);
                  onClose();
                }
              }}
              onError={(error) => console.error('QR Scanner error:', error)}
              constraints={{
                facingMode: 'environment',
              }}
              containerStyle={{
                width: '100%',
                paddingTop: '100%',
                position: 'relative',
              }}
              videoStyle={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
export default ScanOPD;
